<template>
  <div class="record-detail-container">
    <div class="header">
      <van-nav-bar title="答题详情" left-arrow @click-left="goBack" />
    </div>

    <div class="content" v-if="record">
      <div class="summary-card">
        <div class="summary-item">
          <span class="label">小分类</span>
          <span class="value">{{ record.sub_category_name }}</span>
        </div>
        <div class="summary-item">
          <span class="label">答题时间</span>
          <span class="value">{{ formatDate(record.answer_time) }}</span>
        </div>
        <div class="summary-item">
          <span class="label">总用时</span>
          <span class="value">{{ formatTime(record.total_time) }}</span>
        </div>
        <div class="summary-item">
          <span class="label">总得分</span>
          <span class="value" :class="{ 'pending': hasPendingScore }">
            {{ totalScoreSummary }}
          </span>
        </div>
        <div class="summary-item">
          <span class="label">答题数量</span>
          <span class="value">{{ record.question_count }}题</span>
        </div>
      </div>

      <div class="details-section">
        <h2>答题明细</h2>
        
        <div class="detail-list">
          <div 
            v-for="detail in details" 
            :key="detail.id"
            class="detail-item"
          >
            <div class="detail-header">
              <div class="question-info">
                <span class="question-type">{{ getQuestionTypeText(detail.question_type) }}</span>
                <span class="question-title">{{ detail.question_title }}</span>
              </div>
              <van-tag :type="getScoreStatusType(detail)">
                {{ getScoreStatusText(detail) }}
              </van-tag>
            </div>
            
            <div class="detail-body">
              <div class="answer-row">
                <span class="label">用户答案：</span>
                <div class="value user-answer recording-answer" v-if="Number(detail.question_type) === 3">
                  <audio
                    v-if="getRecordingAudioSrc(detail)"
                    :key="getRecordingAudioSrc(detail)"
                    :src="getRecordingAudioSrc(detail)"
                    controls
                    preload="metadata"
                    playsinline
                    webkit-playsinline
                    style="width: 100%; max-width: 300px;"
                    @error="onRecordingAudioError"
                  />
                  <span v-else class="no-audio-hint">暂无录音文件</span>
                </div>
                <span class="value user-answer" v-else>{{ detail.user_answer || '未作答' }}</span>
              </div>
              
              <div class="answer-row" v-if="detail.question_type != 3">
                <span class="label">正确答案：</span>
                <span class="value correct-answer">{{ detail.correct_answer || '无' }}</span>
              </div>
              
              <div class="answer-row" v-if="detail.question_type != 3">
                <span class="label">作答结果：</span>
                <span class="value" :class="getResultClass(detail)">
                  {{ getResultText(detail) }}
                </span>
              </div>
              
              <div class="answer-row score-row" v-if="detail.question_type != 3 && detail.score !== null && detail.score !== undefined">
                <span class="label">得分：</span>
                <span class="value score" :class="getScoreClass(detail)">
                  {{ detail.score }}分
                </span>
              </div>
              
              <div class="answer-row comment-row" v-if="detail.question_type == 3 && detail.comment">
                <span class="label">评价：</span>
                <span class="value comment">{{ detail.comment }}</span>
              </div>
              
              <div class="answer-row score-row" v-if="detail.question_type == 3 && detail.score !== null && detail.score !== undefined">
                <span class="label">评分：</span>
                <span class="value score">{{ detail.score }}分</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pagination-section" v-if="record && totalPages > 1">
        <van-pagination
          v-model="page"
          :total-items="total"
          :items-per-page="pageSize"
          :show-page-size="5"
          force-ellipses
          @change="onPageChange"
        />
      </div>
    </div>

    <van-loading v-else size="24px" vertical>加载中...</van-loading>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { recordingAudioSrc as buildRecordingAudioSrc } from '@/utils/media'

export default {
  name: 'RecordDetail',
  data() {
    return {
      record: null,
      details: [],
      recordId: null,
      pollTimer: null,
      page: 1,
      pageSize: 10,
      loading: false,
      finished: false,
      total: 0,
      totalPages: 1
    }
  },
  computed: {
    /** 录音题是否仍有未评分的明细（或与后台 pending 字段一致） */
    hasPendingScore() {
      if (!this.record) return false
      const detailPending = (this.details || []).some(
        d => Number(d.question_type) === 3 && Number(d.score_status) === 0
      )
      return detailPending || Number(this.record.pending_score_count) > 0
    },
    totalScoreSummary() {
      if (this.hasPendingScore) return '待评分'
      const s = this.record ? Number(this.record.total_score) : 0
      return (isNaN(s) ? 0 : s) + '分'
    }
  },
  async mounted() {
    this.recordId = this.$route.params.id
    await this.loadRecordDetail()
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    goBack() {
      const backRoute = this.$store.getters.smartBackRoute
      this.$router.push(backRoute)
    },
    async loadRecordDetail() {
      try {
        this.loading = true
        const response = await this.$http.get(`/api/answer/record/${this.recordId}`, {
          params: {
            page: this.page,
            page_size: this.pageSize
          }
        })
        const responseData = response.data.data || {}
        
        const oldPendingCount = this.details.filter(d => d.is_recording && d.score_status === 0).length
        const newDetails = responseData.details || []
        const newPendingCount = newDetails.filter(d => d.is_recording && d.score_status === 0).length
        
        this.details = newDetails
        this.record = {
          ...responseData,
          details: newDetails
        }
        
        if (responseData.pagination) {
          this.total = parseInt(responseData.pagination.total) || 0
          this.totalPages = parseInt(responseData.pagination.total_pages) || 1
        }
        
        if (oldPendingCount > newPendingCount) {
          const scoredCount = oldPendingCount - newPendingCount
          Toast.success(`有 ${scoredCount} 道题评分完成`)
          
          if (newPendingCount === 0) {
            this.stopPolling()
          }
        }
        
        this.loading = false
        this.finished = this.page >= this.totalPages
      } catch (err) {
        Toast.fail(err.message)
        this.loading = false
      }
    },
    getQuestionTypeText(type) {
      const types = {
        1: '单选题',
        2: '多选题',
        3: '录音题'
      }
      return types[type] || '未知'
    },
    getScoreStatusText(detail) {
      if (detail.question_type == 3) {
        return detail.score_status === 0 ? '待评分' : '已评分'
      }
      if (detail.question_type == 2) {
        if (detail.is_correct == 1) {
          return '正确'
        } else if (detail.score > 0) {
          return '部分正确'
        } else {
          return '错误'
        }
      }
      return detail.is_correct == 1 ? '正确' : '错误'
    },
    getScoreStatusType(detail) {
      if (detail.question_type == 3) {
        return detail.score_status === 0 ? 'warning' : 'success'
      }
      if (detail.question_type == 2) {
        if (detail.is_correct == 1) {
          return 'success'
        } else if (detail.score > 0) {
          return 'warning'
        } else {
          return 'danger'
        }
      }
      return detail.is_correct == 1 ? 'success' : 'danger'
    },
    getResultText(detail) {
      if (detail.question_type == 2) {
        if (detail.is_correct == 1) {
          return '正确'
        } else if (detail.score > 0) {
          return '部分正确'
        } else {
          return '错误'
        }
      }
      return detail.is_correct == 1 ? '正确' : '错误'
    },
    getResultClass(detail) {
      if (detail.question_type == 2) {
        if (detail.is_correct == 1) {
          return 'correct'
        } else if (detail.score > 0) {
          return 'partial-correct'
        } else {
          return 'wrong'
        }
      }
      return detail.is_correct == 1 ? 'correct' : 'wrong'
    },
    getScoreClass(detail) {
      if (detail.question_type == 2) {
        if (detail.is_correct == 1) {
          return 'correct-score'
        } else if (detail.score > 0) {
          return 'partial-score'
        } else {
          return 'wrong-score'
        }
      }
      return detail.is_correct == 1 ? 'correct-score' : 'wrong-score'
    },
    getRecordingAudioSrc(detail) {
      return buildRecordingAudioSrc(detail)
    },
    onRecordingAudioError() {
      Toast.fail('录音无法播放，请确认文件是否存在或联系管理员')
    },
    formatDate(timestamp) {
      if (!timestamp || timestamp === 0 || isNaN(timestamp)) {
        return '未知时间'
      }
      const date = new Date(timestamp * 1000)
      if (isNaN(date.getTime())) {
        return '未知时间'
      }
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    },
    formatTime(seconds) {
      if (!seconds || seconds === 0 || isNaN(seconds)) {
        return '0秒'
      }
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60
      
      if (hours > 0) {
        return `${hours}小时${minutes}分${secs}秒`
      } else if (minutes > 0) {
        return `${minutes}分${secs}秒`
      } else {
        return `${secs}秒`
      }
    },
    onPageChange(page) {
      this.page = page
      this.loadRecordDetail()
    },
    startPolling() {
      const hasPendingScores = this.details.some(d => d.is_recording && d.score_status === 0)
      
      if (hasPendingScores) {
        this.pollTimer = setInterval(() => {
          this.loadRecordDetail()
        }, 30000)
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    }
  }
}
</script>

<style scoped>
.record-detail-container {
  min-height: 100vh;
  background: #f5f5f5;
}

.header {
  background: white;
}

.content {
  padding: 12px;
  padding-bottom: 120px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item .label {
  font-size: 14px;
  color: #666;
}

.summary-item .value {
  font-size: 14px;
  font-weight: bold;
  color: #333;
}

.summary-item .value.pending {
  color: #faad14;
}

.details-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.details-section h2 {
  font-size: 16px;
  margin-bottom: 16px;
  color: #333;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid #ccc;
}

.detail-item.correct {
  border-left-color: #52c41a;
}

.detail-item.wrong {
  border-left-color: #ff4d4f;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e8e8;
}

.question-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.question-type {
  font-size: 12px;
  padding: 2px 8px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 4px;
}

.question-title {
  font-size: 14px;
  color: #333;
  flex: 1;
}

.detail-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.answer-row {
  display: flex;
  gap: 8px;
  font-size: 13px;
}

.answer-row .label {
  min-width: 70px;
  color: #666;
}

.answer-row .value {
  flex: 1;
  color: #333;
}

.answer-row .value.correct-answer {
  color: #52c41a;
  font-weight: bold;
}

.answer-row .value.user-answer {
  color: #1890ff;
  font-weight: 500;
}

.recording-answer .no-audio-hint {
  color: #999;
  font-size: 13px;
}

.answer-row .value.correct {
  color: #52c41a;
  font-weight: bold;
}

.answer-row .value.partial-correct {
  color: #faad14;
  font-weight: bold;
}

.answer-row .value.wrong {
  color: #ff4d4f;
  font-weight: bold;
}

.answer-row.comment-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e8e8e8;
}

.answer-row.score-row {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e8e8e8;
}

.answer-row .value.comment {
  color: #52c41a;
  font-style: italic;
  line-height: 1.6;
}

.answer-row .value.score {
  color: #faad14;
  font-weight: bold;
  font-size: 15px;
}

.answer-row .value.score.correct-score {
  color: #52c41a;
}

.answer-row .value.score.partial-score {
  color: #faad14;
}

.answer-row .value.score.wrong-score {
  color: #ff4d4f;
}

.pagination-section {
  margin-top: 20px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
</style>
